// Typed fetch wrapper. Reads Bearer from localStorage["killacoach_api_key"].
// On 401, prompts the user once and retries. Prompt is skipped in non-browser envs.

import type {
  AgentListItem,
  AgentSummary,
  Call,
  CallDetail,
  CallsList,
  CoachScorecard,
  LeaderboardRow,
  Review,
  ReviewListResponse,
  ReviewSubmission,
  Rubric,
  Stats,
} from './types'

const STORAGE_KEY = 'killacoach_api_key'

function getKey(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function setKey(k: string) {
  try {
    localStorage.setItem(STORAGE_KEY, k)
  } catch {
    // ignore — private-mode etc.
  }
}

function promptForKey(): string | null {
  if (typeof window === 'undefined' || typeof window.prompt !== 'function') {
    return null
  }
  const entered = window.prompt(
    'killacoach API key required (stored in localStorage):'
  )
  if (!entered) return null
  setKey(entered)
  return entered
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  // When true, returns raw Response instead of parsed JSON (for audio streams).
  raw?: boolean
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = 'GET', body, signal, raw = false } = opts
  const headers: Record<string, string> = {
    Accept: raw ? '*/*' : 'application/json',
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const key = getKey()
  if (key) headers.Authorization = `Bearer ${key}`

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  if (res.status === 401) {
    const newKey = promptForKey()
    if (newKey) {
      // Retry once with new key.
      headers.Authorization = `Bearer ${newKey}`
      const retry = await fetch(path, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
      })
      if (!retry.ok) {
        throw new Error(`${retry.status}: ${await safeText(retry)}`)
      }
      return raw ? ((retry as unknown) as T) : ((await retry.json()) as T)
    }
    throw new Error('401: unauthorized')
  }

  if (!res.ok) {
    throw new Error(`${res.status}: ${await safeText(res)}`)
  }

  return raw ? ((res as unknown) as T) : ((await res.json()) as T)
}

async function safeText(res: Response): Promise<string> {
  try {
    const j = await res.json()
    if (j && typeof j === 'object' && 'error' in j) return String((j as { error: unknown }).error)
    return JSON.stringify(j)
  } catch {
    try {
      return await res.text()
    } catch {
      return res.statusText
    }
  }
}

function qs(params: Record<string, unknown>): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    if (typeof v === 'boolean') {
      parts.push(`${encodeURIComponent(k)}=${v ? 'true' : 'false'}`)
    } else {
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    }
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

// ---- public surface ----

export interface CallsListQuery {
  agent?: string
  dispo?: string
  min_overall?: number
  max_overall?: number
  dispo_mismatch?: boolean
  since?: string // ISO
  until?: string // ISO
  limit?: number
  offset?: number
  sort?: string
}

export const api = {
  health: () => request<{ ok: boolean; version: string; db_path: string }>('/health'),

  stats: () => request<Stats>('/api/stats'),

  rubric: () => request<Rubric>('/api/rubric'),

  agents: {
    list: () => request<AgentListItem[]>('/api/agents'),
    summary: (vici_user: string) =>
      request<AgentSummary>(`/api/agents/${encodeURIComponent(vici_user)}/summary`),
  },

  calls: {
    list: (q: CallsListQuery = {}) =>
      request<CallsList>(`/api/calls${qs(q as Record<string, unknown>)}`),
    get: (id: number) => request<CallDetail>(`/api/calls/${id}`),
    raw: (id: number) => request<Call>(`/api/calls/${id}`),
    audioUrl: (id: number) => `/api/calls/${id}/audio`,
  },

  leaderboard: () => request<LeaderboardRow[]>('/api/leaderboard'),

  coachScorecard: (since: string, until: string) =>
    request<CoachScorecard>(`/api/coach-scorecard${qs({ since, until })}`),

  admin: {
    rescore: (id: number) =>
      request<{ queued: boolean }>(`/api/admin/rescore/${id}`, { method: 'POST' }),
  },

  reviews: {
    submit: (call_id: number, payload: ReviewSubmission) =>
      request<Review>(`/api/calls/${call_id}/review`, {
        method: 'POST',
        body: payload,
      }),
    list: (call_id: number) =>
      request<ReviewListResponse>(`/api/calls/${call_id}/reviews`),
    patch: (call_id: number, review_id: number, payload: ReviewSubmission) =>
      request<Review>(`/api/calls/${call_id}/review/${review_id}`, {
        method: 'PATCH',
        body: payload,
      }),
  },
}

export { getKey as getApiKey, setKey as setApiKey }
